import useKeyHandler from './useKeyHandler';

/**
 * useKeyboardNav — WASD + Arrow + Shift+PageUp/Down keyboard navigation.
 * Reusable hook for any list with paging.
 *
 * @param {object} params
 * @param {Array}    params.entries      - current page entries
 * @param {string}   params.selectedRk   - rk of selected entry
 * @param {Function} params.onSelect     - fn(entry) called on selection change
 * @param {object}   params.meta         - { page }
 * @param {number}   params.totalPages   - total number of pages
 * @param {Function} params.onPageChange - fn(page, direction) called on page navigation
 * @param {boolean}  params.disabled     - block navigation when true
 */
export default function useKeyboardNav({ entries, selectedRk, onSelect, meta, totalPages, onPageChange, disabled = false }) {
    /**
     * Handles keyboard navigation within the current entry list and page.
     *
     * W / ArrowUp   — select previous entry, or jump to previous page (selects last entry)
     * S / ArrowDown — select next entry, or jump to next page (selects first entry)
     * Shift+PageUp  — jump to previous page
     * Shift+PageDown — jump to next page
     *
     * Cross-node navigation (jumping between nodes) is handled separately by useCrossNodeNav.
     * Navigation is blocked when disabled=true, e.g. when JInspector is dirty.
     *
     * @param {object} keys - destructured key flags from useKeyHandler
     */
    const handleKey = ({ isUp, isDown, isPageUp, isPageDown }) => {
        if (disabled) return;

        const currentIndex = entries.findIndex((entry) => entry.rk === selectedRk);

        if (isUp && currentIndex > 0) {
            onSelect(entries[currentIndex - 1]);
            return;
        }

        if (isDown && currentIndex < entries.length - 1) {
            onSelect(entries[currentIndex + 1]);
            return;
        }

        // Page boundary — S on last entry goes to next page, W on first entry goes to prev page
        if (isDown && currentIndex === entries.length - 1 && meta.page < totalPages) {
            onPageChange(meta.page + 1, 'next');
            return;
        }

        if (isUp && currentIndex === 0 && meta.page > 1) {
            onPageChange(meta.page - 1, 'prev');
            return;
        }

        // Shift+PageUp/Down — explicit page jump
        if (isPageUp && meta.page > 1) {
            onPageChange(meta.page - 1, 'prev');
            return;
        }

        if (isPageDown && meta.page < totalPages) {
            onPageChange(meta.page + 1, 'next');
            return;
        }
    };

    useKeyHandler({
        onKey: handleKey,
        deps: [entries, selectedRk, meta, totalPages, onSelect, onPageChange, disabled],
    });
}
